// Compute the key at call time so it uses the pathname of the failing route,
// not the pathname of the initial boot (they differ after SPA navigation).
function getCssChunkReloadKey(): string {
  return `css_chunk_reload_${globalThis.location.pathname}`;
}

// Snapshot captured at module-evaluation time (before any error handler or Sentry fires).
// Use this in beforeSend instead of reading sessionStorage at error time, because by the time
// beforeSend runs the key has already been written by the error listener.
let _keyExistedAtBoot = false;
try {
  _keyExistedAtBoot = !!sessionStorage.getItem(getCssChunkReloadKey());
} catch {
  // sessionStorage unavailable — treat as absent (first failure)
}
export const cssChunkKeyExistedAtBoot = _keyExistedAtBoot;

export function isCssChunkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: string; message?: string };
  const msg = typeof err.message === 'string' ? err.message.toLowerCase() : '';
  // Matches original rspack error (code) and legacy message format.
  // Also matches the pluginAssetsRetry "after N retries" format:
  // e.g. "Loading chunk X from 'static/css/async/X.hash.css' failed after 3 retries: '...'"
  return (
    err.code === 'CSS_CHUNK_LOAD_FAILED' ||
    msg.includes('loading css chunk') ||
    (msg.includes('failed after') && msg.includes('static/css/'))
  );
}

/**
 * Writes the reload key if not already set. Returns true if the key was newly written
 * (first failure → caller should reload), false if it was already present or sessionStorage
 * is unavailable (persistent failure or storage unavailable → caller should not reload).
 */
export function writeCssChunkReloadKeyIfAbsent(): boolean {
  try {
    const key = getCssChunkReloadKey();
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function cleanCssChunkReloadKey(): void {
  try {
    sessionStorage.removeItem(getCssChunkReloadKey());
  } catch {
    // suppress
  }
}
